import { HOME_ASSETS } from "@/home/home-assets";
import {
  KIND_PICKER_KINDS,
  kindPreset,
  normalizePartKind,
  type HomePartKind,
} from "@/home/part-kind";

export type KindPickerHandle = {
  root: HTMLElement;
  getValue: () => HomePartKind;
  setValue: (kind: HomePartKind) => void;
};

function kindIconSrc(kind: HomePartKind): string {
  return HOME_ASSETS.shopTabs[kind];
}

export function mountKindPicker(
  selected: HomePartKind,
  options?: {
    id?: string;
    compact?: boolean;
    showHint?: boolean;
    kinds?: HomePartKind[];
    /** Icon + label dropdown (game UI). */
    variant?: "text" | "icons" | "select";
  },
): KindPickerHandle {
  const pickerKinds = options?.kinds?.length ? options.kinds : KIND_PICKER_KINDS;
  const initial = pickerKinds.includes(selected) ? selected : pickerKinds[0] ?? "solid";
  const hidden = document.createElement("input");
  hidden.type = "hidden";
  hidden.className = "home-kind-picker-value";
  if (options?.id) hidden.id = options.id;
  hidden.value = selected;

  const variant = options?.variant ?? "text";
  const iconMode = variant === "icons";
  const selectMode = variant === "select";

  const grid = document.createElement("div");
  grid.className = [
    "home-kind-picker",
    options?.compact ? "home-kind-picker--compact" : "",
    iconMode ? "home-kind-picker--icons" : "",
  ]
    .filter(Boolean)
    .join(" ");
  grid.setAttribute("role", "radiogroup");
  grid.setAttribute("aria-label", "หมวดชิ้น");

  const hint = document.createElement("p");
  hint.className = "hint home-kind-picker-hint";
  if (options?.showHint === false) hint.hidden = true;

  const wrap = document.createElement("div");
  wrap.className = [
    "home-kind-picker-wrap",
    options?.compact ? "home-kind-picker-wrap--compact" : "",
    selectMode ? "home-kind-picker-wrap--select" : "",
  ]
    .filter(Boolean)
    .join(" ");

  let dropdown: HTMLElement | null = null;
  let triggerBtn: HTMLButtonElement | null = null;
  let triggerIcon: HTMLImageElement | null = null;
  let triggerLabel: HTMLElement | null = null;
  let menuEl: HTMLElement | null = null;
  let menuOpen = false;
  let onDocPointer: ((event: PointerEvent) => void) | null = null;

  const closeMenu = () => {
    if (!menuOpen) return;
    menuOpen = false;
    if (menuEl) menuEl.hidden = true;
    triggerBtn?.setAttribute("aria-expanded", "false");
    if (onDocPointer) {
      document.removeEventListener("pointerdown", onDocPointer, true);
      onDocPointer = null;
    }
  };

  const openMenu = () => {
    if (!menuEl || !triggerBtn) return;
    menuOpen = true;
    menuEl.hidden = false;
    triggerBtn.setAttribute("aria-expanded", "true");
    onDocPointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && wrap.contains(target)) return;
      closeMenu();
    };
    document.addEventListener("pointerdown", onDocPointer, true);
  };

  const syncDropdownUi = (kind: HomePartKind) => {
    if (!selectMode || !dropdown) return;
    const preset = kindPreset(kind);
    if (triggerIcon) {
      triggerIcon.src = kindIconSrc(kind);
      triggerIcon.alt = "";
    }
    if (triggerLabel) triggerLabel.textContent = preset.labelTh;
    if (triggerBtn) triggerBtn.title = preset.hintTh;
    menuEl?.querySelectorAll<HTMLButtonElement>("[data-kind]").forEach((btn) => {
      const k = btn.dataset.kind as HomePartKind | undefined;
      const on = k === kind;
      btn.classList.toggle("home-kind-dropdown-option--on", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    });
  };

  const apply = (kind: HomePartKind) => {
    const normalized = normalizePartKind(kind);
    const resolved = pickerKinds.includes(normalized) ? normalized : (pickerKinds[0] ?? "solid");
    hidden.value = resolved;
    const preset = kindPreset(resolved);
    if (!hint.hidden) hint.textContent = preset.hintTh;
    if (selectMode) {
      syncDropdownUi(resolved);
      closeMenu();
    } else {
      grid.querySelectorAll<HTMLButtonElement>("[data-kind]").forEach((btn) => {
        const k = btn.dataset.kind as HomePartKind | undefined;
        const on = k === resolved;
        btn.classList.toggle("home-kind-picker-chip--on", on);
        btn.setAttribute("aria-checked", on ? "true" : "false");
      });
    }
    wrap.dispatchEvent(
      new CustomEvent("home-kind-change", {
        bubbles: true,
        detail: { kind: resolved },
      }),
    );
  };

  if (selectMode) {
    dropdown = document.createElement("div");
    dropdown.className = "home-kind-dropdown";

    triggerBtn = document.createElement("button");
    triggerBtn.type = "button";
    triggerBtn.className = "home-kind-dropdown-trigger";
    triggerBtn.setAttribute("aria-haspopup", "listbox");
    triggerBtn.setAttribute("aria-expanded", "false");
    if (options?.id) triggerBtn.id = `${options.id}-trigger`;

    triggerIcon = document.createElement("img");
    triggerIcon.className = "home-kind-dropdown-icon";
    triggerIcon.width = 22;
    triggerIcon.height = 22;
    triggerIcon.decoding = "async";

    triggerLabel = document.createElement("span");
    triggerLabel.className = "home-kind-dropdown-label";

    const chevron = document.createElement("span");
    chevron.className = "home-kind-dropdown-chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.textContent = "▾";

    triggerBtn.append(triggerIcon, triggerLabel, chevron);
    triggerBtn.addEventListener("click", () => {
      if (menuOpen) closeMenu();
      else openMenu();
    });

    menuEl = document.createElement("div");
    menuEl.className = "home-kind-dropdown-menu";
    menuEl.setAttribute("role", "listbox");
    menuEl.setAttribute("aria-label", "หมวดชิ้น");
    menuEl.hidden = true;

    for (const kind of pickerKinds) {
      const preset = kindPreset(kind);
      const opt = document.createElement("button");
      opt.type = "button";
      opt.className = "home-kind-dropdown-option";
      opt.dataset.kind = kind;
      opt.setAttribute("role", "option");
      opt.title = preset.hintTh;
      opt.innerHTML = `
        <img class="home-kind-dropdown-icon" src="${kindIconSrc(kind)}" alt="" width="22" height="22" decoding="async" />
        <span class="home-kind-dropdown-option-label">${preset.labelTh}</span>
      `;
      opt.addEventListener("click", () => apply(kind));
      menuEl.append(opt);
    }

    dropdown.append(triggerBtn, menuEl);
    wrap.append(hidden, dropdown, hint);
  } else {
    for (const kind of pickerKinds) {
      const preset = kindPreset(kind);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = iconMode
        ? "home-kind-picker-chip home-kind-picker-chip--icon"
        : "home-kind-picker-chip";
      btn.dataset.kind = kind;
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", kind === initial ? "true" : "false");
      btn.setAttribute("aria-label", preset.tabLabelTh);
      btn.title = iconMode ? preset.tabLabelTh : preset.hintTh;
      if (iconMode) {
        const src = kindIconSrc(kind);
        btn.innerHTML = `<img class="home-kind-picker-chip-icon" src="${src}" alt="" width="24" height="24" decoding="async" /><span class="visually-hidden">${preset.tabLabelTh}</span>`;
      } else {
        btn.innerHTML = `<span class="home-kind-picker-chip-label">${preset.tabLabelTh}</span>`;
      }
      btn.addEventListener("click", () => apply(kind));
      grid.append(btn);
    }
    wrap.append(hidden, grid, hint);
  }

  apply(initial);

  return {
    root: wrap,
    getValue: () => {
      const k = normalizePartKind(hidden.value);
      return pickerKinds.includes(k) ? k : (pickerKinds[0] ?? "solid");
    },
    setValue: (kind: HomePartKind) => {
      const k = pickerKinds.includes(kind) ? kind : (pickerKinds[0] ?? "solid");
      apply(k);
    },
  };
}
